import { ChangeDetectionStrategy, Component, Inject, inject, signal, OnInit } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { clearControl } from './clearable-field.type';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { ReactiveFormsModule } from '@angular/forms';
import { DatePipe } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { EntriesService } from 'app/features/admin/modules/features/entries/entries.service';
import { map, takeUntil } from 'rxjs';
import { GenericEntry } from 'app/api/models';
import { ModulesStore } from 'app/features/admin/modules/modules.store';
import { UtilityService } from '../../services/utility.service';
import { cloneDeep } from 'lodash-es';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { commitUserValue } from './user-edit.util';

export interface DocumentsMetaData {
  blobReference?: string | null;
  blobUrl?: string | null;
  createdDateTime?: string;
  documentId?: string;
  documentName?: string | null;
}

export interface DocumentsMetaDataWithIcon extends DocumentsMetaData {
  fileIcon: string;
  fileTypeLabel: string;
}

const IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg']);

function getFileExtension(filename: string | null | undefined): string | undefined {
  return filename?.split(/[?#]/, 1)[0].split('.').pop()?.toLowerCase();
}

export function isImageFile(filename: string | null | undefined): boolean {
  const extension = getFileExtension(filename);
  return extension !== undefined && IMAGE_EXTENSIONS.has(extension);
}

export function getMediaFileTypeLabel(filename: string | null | undefined): string {
  const extension = getFileExtension(filename);
  if (!extension) return 'File';

  switch (extension) {
    case 'pdf':
      return 'PDF Document';
    case 'doc':
    case 'docx':
      return 'Word Document';
    case 'mp4':
    case 'avi':
    case 'mov':
      return 'Video File';
    case 'mp3':
    case 'wav':
      return 'Audio File';
    case 'jpg':
    case 'jpeg':
    case 'png':
    case 'gif':
    case 'webp':
    case 'svg':
      return `Image (${extension.toUpperCase()})`;
    default:
      return `File (${extension.toUpperCase()})`;
  }
}

// Media Picker Dialog Component
@Component({
  selector: 'app-media-picker-dialog',
  template: `
    <div class="flex max-h-[90vh] min-h-0 flex-col overflow-hidden p-6">
      <div class="mb-6 flex shrink-0 items-center justify-between">
        <h2 class="text-xl font-semibold">Selecteer Media</h2>
        <div class="flex items-center gap-2">
          <input #fileInput type="file" hidden (change)="onUploadFileSelected($event)" />
          <button mat-stroked-button [disabled]="isUploading()" (click)="fileInput.click()">
            <mat-icon>upload</mat-icon>
            {{ isUploading() ? 'Uploaden…' : 'Upload bestand' }}
          </button>
          <button mat-icon-button (click)="dialog.close()">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto pr-2">
        <div class="grid grid-cols-4 gap-4">
          @for (item of documents(); track item.documentId) {
            <div
              class="border rounded-lg p-2 cursor-pointer transition-colors bg-light-tint"
              [class.border-primary]="selectedItem?.documentId === item.documentId"
              [attr.data-document-id]="item.documentId"
              (click)="selectDocument(item)">
              <div class="aspect-square mb-2">
                @if (isImage(item.blobReference)) {
                  <img
                    [src]="item.blobUrl"
                    [alt]="item.documentName || 'Document'"
                    class="media-preview-background w-full h-full object-contain rounded" />
                } @else {
                  <div class="w-full h-full flex items-center justify-center rounded">
                    <mat-icon class="text-4xl">{{ item.blobReference }}</mat-icon>
                  </div>
                }
              </div>
              <div class="text-sm truncate">{{ item.documentName || item.blobReference }}</div>
            </div>
          }
        </div>
      </div>

      <div class="mt-6 flex shrink-0 justify-end gap-2">
        <button mat-stroked-button (click)="dialog.close()">Annuleer</button>
        <button
          mat-flat-button
          class="dd-action-button"
          [disabled]="!selectedItem"
          (click)="dialog.close(selectedItem)">
          Selecteer
        </button>
      </div>
    </div>
  `,
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./media-picker.component.scss'],
})
class MediaPickerDialogComponent {
  dialog = inject(MatDialogRef);
  entriesService = inject(EntriesService);
  modulesStore = inject(ModulesStore);
  readonly #dialogData = inject<{ selected?: DocumentsMetaData | null } | null>(MAT_DIALOG_DATA, {
    optional: true,
  });
  private utilityService = inject(UtilityService);
  private destroy$ = injectDestroy();
  documents = signal<DocumentsMetaDataWithIcon[]>([]);
  isLoading = signal(true);
  isUploading = signal(false);
  selectedItem?: DocumentsMetaDataWithIcon;

  constructor() {
    this.fetchDocuments();
  }

  onUploadFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Reset so re-picking the same file fires the change event again.
    input.value = '';
    if (file) {
      this.uploadFile(file);
    }
  }

  /**
   * Upload a file as a new public document in the 'documents' module so it
   * becomes pickable like the existing entries. Goes through the regular
   * entry+file flow (EntriesService.addEntry, same as the documents-module UI):
   * a bare createModuleEntry + document POST leaves the entry without
   * entry.archived and without persisted document metadata, so fetchDocuments
   * (filter `entry.archived eq false` + non-empty publicDocuments) never
   * returns it again after the dialog reopens.
   */
  private uploadFile(file: File) {
    this.isUploading.set(true);
    const moduleId = this.modulesStore.moduleCodeToId('documents');
    // addEntry expects a FileList; wrap the single picked file.
    const fileList = new DataTransfer();
    fileList.items.add(file);

    this.entriesService
      .addEntry(
        moduleId,
        { name: file.name, archived: false },
        { fieldKey: 'entry.file', files: fileList.files, isPublic: true }
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          this.isUploading.set(false);
          const document = (response.publicDocuments?.[0] ?? response.entry?.file) as
            | DocumentsMetaDataWithIcon
            | undefined;
          if (!document?.blobReference) {
            this.utilityService.notify('Upload mislukt: geen document ontvangen');
            return;
          }
          document.fileIcon = this.getFileIcon(document.blobReference);
          document.fileTypeLabel = this.getFileTypeLabel(document.blobReference);
          this.documents.update(documents => [document, ...documents]);
          this.selectedItem = document;
        },
        error: error => {
          console.error('Error uploading document:', error);
          this.isUploading.set(false);
          this.utilityService.notify('Upload mislukt');
        },
      });
  }

  private fetchDocuments(): void {
    this.isLoading.set(true);

    const moduleId = this.modulesStore.moduleCodeToId('documents');
    this.entriesService
      .getEntries({
        moduleId,
        offset: 0,
        orderBy: { active: 'entry.name', direction: 'asc' },
        limit: 1000,
      })
      .pipe(
        map(entries =>
          entries
            .filter((entry: GenericEntry) => entry.publicDocuments && entry.publicDocuments.length > 0)
            .map((entry: GenericEntry) => {
              const document = entry.publicDocuments[0] as DocumentsMetaDataWithIcon;
              document.fileIcon = this.getFileIcon(document.blobReference);
              return document;
            })
            // Newest first, so fresh uploads surface at the top of the grid.
            .sort((a, b) => (b.createdDateTime ?? '').localeCompare(a.createdDateTime ?? ''))
        )
      )
      .subscribe({
        next: documents => {
          const docs = cloneDeep(documents);
          this.documents.set(docs);
          this.isLoading.set(false);
          this.#restoreSelection(docs);
        },
        error: error => {
          console.error('Error fetching documents:', error);
          this.isLoading.set(false);
        },
      });
  }

  /**
   * Reselect the media the field currently holds and scroll it into view.
   * The grid is sorted alphabetically, so without this a (recently uploaded)
   * selection often sits below the dialog fold and looks "gone" on reopen.
   */
  #restoreSelection(documents: DocumentsMetaDataWithIcon[]) {
    const selectedId = this.#dialogData?.selected?.documentId;
    if (!selectedId) return;

    const match = documents.find(document => document.documentId === selectedId);
    if (!match) return;

    this.selectedItem = match;
    setTimeout(() => {
      window.document
        .querySelector(`[data-document-id="${selectedId}"]`)
        ?.scrollIntoView({ block: 'center', behavior: 'instant' });
    });
  }

  getFileIcon(filename: string | null | undefined): string {
    if (!filename) return 'insert_drive_file';
    const ext = filename.split('.').pop()?.toLowerCase();

    switch (ext) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'doc':
      case 'docx':
        return 'description';
      case 'mp4':
      case 'avi':
      case 'mov':
        return 'movie';
      case 'mp3':
      case 'wav':
        return 'audio_file';
      default:
        return 'insert_drive_file';
    }
  }

  getFileTypeLabel(filename: string | null | undefined): string {
    return getMediaFileTypeLabel(filename);
  }

  selectDocument(item: DocumentsMetaDataWithIcon) {
    this.selectedItem = item;
  }

  isImage(filename: string | null | undefined): boolean {
    return isImageFile(filename);
  }
}

// Main Formly Field Component
@Component({
  selector: 'formly-field-media-picker',
  template: `
    <div class="mb-4">
      @if (props.label) {
        <label class="font-medium inline-block mb-2">{{ props.label }}</label>
      }

      <div class="flex flex-col gap-4">
        @if (selectedMedia()) {
          <div
            class="border rounded-lg p-4 bg-light-tint"
            [class.cursor-pointer]="props.disabled && isImage(selectedMedia().blobReference)"
            (click)="props.disabled && isImage(selectedMedia().blobReference) && openPreviewModal()">
            <div class="flex items-start gap-4">
              <div class="w-32 aspect-square border rounded-lg overflow-hidden">
                @if (isImage(selectedMedia().blobReference)) {
                  <img
                    [src]="selectedMedia().blobUrl"
                    [alt]="selectedMedia().documentName || 'Document'"
                    class="media-preview-background w-full h-full object-contain" />
                } @else {
                  <div class="w-full h-full flex items-center justify-center">
                    <mat-icon class="text-4xl">{{ selectedMedia().fileIcon }}</mat-icon>
                  </div>
                }
              </div>
              <div class="flex flex-col gap-1">
                <div class="font-medium text-sm">
                  {{ selectedMedia().documentName || selectedMedia().blobReference }}
                </div>
                <div class="text-xs">{{ selectedMedia().fileTypeLabel }}</div>
                @if (selectedMedia().createdDateTime) {
                  <div class="text-xs text-accent">
                    Aangemaakt op: {{ selectedMedia().createdDateTime | date: 'medium' }}
                  </div>
                }
              </div>
            </div>
          </div>
        }

        @if (!props.disabled) {
          <div class="flex flex-col gap-2">
            <button type="button" mat-stroked-button (click)="openMediaPicker()">
              {{ selectedMedia() ? 'Wijzig media' : 'Kies bestand' }}
            </button>

            @if (selectedMedia()) {
              <button type="button" mat-stroked-button color="warn" (click)="clearSelection()">Verwijder</button>
            }
          </div>
        }

        @if (props.description) {
          <div class="text-sm mt-2 text-accent-400">{{ props.description }}</div>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormlyModule, MatButtonModule, MatDialogModule, ReactiveFormsModule, MatIconModule, DatePipe],
  styleUrls: ['./media-picker.component.scss'],
})
export class FormlyFieldMediaPickerComponent extends FieldType implements OnInit {
  private dialog = inject(MatDialog);
  private destroy$ = injectDestroy();
  selectedMedia = signal<DocumentsMetaDataWithIcon | null>(null);

  ngOnInit() {
    if (this.formControl.value) {
      const documentWithIcon = this.formControl.value as DocumentsMetaDataWithIcon;
      documentWithIcon.fileIcon = this.getFileIcon(documentWithIcon.blobReference);
      documentWithIcon.fileTypeLabel = this.getFileTypeLabel(documentWithIcon.blobReference);
      this.selectedMedia.set(documentWithIcon);
    }
  }

  openMediaPicker() {
    const dialogRef = this.dialog.open(MediaPickerDialogComponent, {
      width: '800px',
      maxHeight: '90vh',
      panelClass: 'media-picker-dialog',
      data: { selected: this.selectedMedia() },
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: DocumentsMetaDataWithIcon | undefined) => {
        if (result) {
          this.selectedMedia.set(result);
          // Mirrors clearSelection's contract below: dirty + touched, so the entry save picks it up.
          commitUserValue(this.formControl, result);
        }
      });
  }

  clearSelection() {
    this.selectedMedia.set(null);
    // Shared contract: null + dirty + touched, so the entry save picks the wipe up.
    clearControl(this.formControl, this.props);
  }

  isImage(filename: string | null | undefined): boolean {
    return isImageFile(filename);
  }

  getFileIcon(filename: string | null | undefined): string {
    if (!filename) return 'insert_drive_file';
    const ext = filename.split('.').pop()?.toLowerCase();

    switch (ext) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'doc':
      case 'docx':
        return 'description';
      case 'mp4':
      case 'avi':
      case 'mov':
        return 'movie';
      case 'mp3':
      case 'wav':
        return 'audio_file';
      default:
        return 'insert_drive_file';
    }
  }

  getFileTypeLabel(filename: string | null | undefined): string {
    return getMediaFileTypeLabel(filename);
  }

  openPreviewModal() {
    if (!this.selectedMedia() || !this.isImage(this.selectedMedia().blobReference) || !this.props.disabled) return;

    this.dialog.open(MediaPreviewDialogComponent, {
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: this.selectedMedia(),
      panelClass: 'image-preview-dialog',
    });
  }
}

@Component({
  selector: 'app-image-preview-dialog',
  template: `
    <div class="p-4">
      <img
        [src]="data.blobUrl"
        [alt]="data.documentName || 'Preview'"
        class="media-preview-background max-w-full max-h-[80vh] object-contain mx-auto" />
    </div>
  `,
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./media-picker.component.scss'],
})
export class MediaPreviewDialogComponent {
  constructor(
    public dialogRef: MatDialogRef<MediaPreviewDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DocumentsMetaDataWithIcon
  ) {}
}
